import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  InputAdornment,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';

import { useServices } from 'services';
import HorizontallyCentered from 'components/core/HorizontallyCentered';
import CircularProgress from 'components/core/CircularProgress';
import H1 from 'components/core/H1';

import useLoadingStateManager from 'hooks/useLoadingStateManager';
import useUserMemberships from 'hooks/useUserMemberships';
import useUserApproval from 'hooks/useUserApproval';
import User from 'types/User';
import { MembershipTier } from 'types/SignUpData';
import { TIER_CONFIG } from 'components/project/TierChip';

import AllUsersTab from './AllUsersTab';
import PendingRequestsTab from './PendingUsersRequestsTab';
import EditUserDialog from './EditUserDialog';
import ReviewRequestDialog from './ReviewRequestDialog';
import { UnifiedRequest } from 'types/ManageUserMemberships';
import { matchesSearch, buildPendingRows } from 'utils/manageMemberships';

const StyledAlert = styled(Alert)({ marginBottom: '1rem' });
const StyledH1 = styled(H1)(({ theme }) => ({
  color: theme?.palette?.primary?.main || '#1976d2',
  marginBottom: 0,
  textAlign: 'center',
  paddingTop: '16px',
}));
const CenteredContainer = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '1.5rem',
});

export interface ManageUserMembershipsProps {
  className?: string;
}

const ManageUserMemberships: React.FC<ManageUserMembershipsProps> = ({ className }) => {
  const { authService } = useServices();
  const navigate = useNavigate();
  const loadingStateManager = useLoadingStateManager();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const {
    users,
    isLoading: membershipsLoading,
    fetchAllUsers,
    adminUpdateMembershipTier,
    postAdminComment,
    enableUserAccess,
    restrictUserAccess,
    persistedCommentByEmail,
  } = useUserMemberships();

  const {
    pendingUsers,
    pendingUpgradeRequests,
    recentlyReviewedUsers,
    isLoading: approvalLoading,
    fetchUsers,
    approveUser,
    denyUser,
    resolveTierUpgrade,
  } = useUserApproval();

  const reviewCommentByEmail = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of recentlyReviewedUsers) {
      if (u.email && u.adminComment) map[u.email] = u.adminComment;
    }
    return map;
  }, [recentlyReviewedUsers]);

  const [localCommentByEmail, setLocalCommentByEmail] = useState<Record<string, string>>({});

  const effectiveCommentByEmail = useMemo(
    () => ({ ...persistedCommentByEmail, ...reviewCommentByEmail, ...localCommentByEmail }),
    [persistedCommentByEmail, reviewCommentByEmail, localCommentByEmail]
  );

  const [activeTab, setActiveTab] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [savingEmail, setSavingEmail] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editTier, setEditTier] = useState<MembershipTier>('Tier 1');
  const [editAdminComment, setEditAdminComment] = useState('');

  // Review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRow, setReviewRow] = useState<UnifiedRequest | null>(null);
  const [reviewTier, setReviewTier] = useState<MembershipTier>('Tier 1');
  const [reviewComment, setReviewComment] = useState('');

  const currentUser = authService.user;
  const isLoading = membershipsLoading || approvalLoading;

  // Tracks whether the initial parallel fetch has fully completed.
  // Using a dedicated flag avoids the race where one of the two parallel
  // fetches populates its list mid-load, causing the spinner to hide early
  // while the other fetch is still in flight.
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // --- Fetch on mount ---
  useEffect((): void => {
    const loadAll = async (): Promise<void> => {
      await Promise.all([fetchAllUsers(), fetchUsers()]);
      setInitialLoadComplete(true);
    };
    loadingStateManager.startLoading({ waitOn: loadAll() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Filtered data ---
  const filteredAllUsers = users.filter((u) => matchesSearch(u, searchText));
  const filteredPendingNew = pendingUsers.filter((u) => matchesSearch(u, searchText));
  const filteredPendingUpgrade = pendingUpgradeRequests.filter((u) =>
    matchesSearch(u, searchText)
  );
  const pendingRows = buildPendingRows(filteredPendingNew, filteredPendingUpgrade);
  const totalPendingCount = pendingUsers.length + pendingUpgradeRequests.length;

  // --- Edit handlers ---
  const handleOpenEdit = (user: User): void => {
    setEditUser(user);
    setEditTier((user.membershipTier as MembershipTier) || 'Tier 1');
    // Always start empty — previous comments are visible in UserHistoryPanel below
    setEditAdminComment('');
    setEditDialogOpen(true);
  };
  const handleCloseEdit = (): void => {
    setEditDialogOpen(false);
    setEditUser(null);
    setEditAdminComment('');
  };
  const handleSaveEdit = async (): Promise<void> => {
    if (!editUser) return;
    const tierChanged = editTier !== editUser.membershipTier;
    const commentChanged = editAdminComment !== (editUser.adminComment || '');
    if (!tierChanged && !commentChanged) {
      handleCloseEdit();
      return;
    }
    try {
      setSavingEmail(editUser.email);
      if (tierChanged) {
        // /admin-tier-update/ handles both the tier change and an optional comment
        await adminUpdateMembershipTier(editUser, editTier, editAdminComment || undefined);
        await fetchAllUsers();
      } else if (editAdminComment.trim()) {
        // Comment-only change — use the dedicated endpoint (only if non-empty)
        await postAdminComment(editUser, editAdminComment);
      }
      // Persist the new comment locally so the table reflects it immediately
      // (Cognito doesn't store adminComment, so fetchAllUsers won't return it)
      if (commentChanged) {
        setLocalCommentByEmail((prev) => ({
          ...prev,
          [editUser.email]: editAdminComment,
        }));
        editUser.adminComment = editAdminComment || undefined;
      }
      editUser.lastUpdated = new Date().toISOString();
      setSnackbar({
        open: true,
        message: tierChanged
          ? `Updated ${editUser.firstName} ${editUser.lastName} to ${TIER_CONFIG[editTier]?.label || editTier}`
          : `Updated admin comment for ${editUser.firstName} ${editUser.lastName}`,
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: `Failed to update ${editUser.email}`,
        severity: 'error',
      });
    } finally {
      setSavingEmail(null);
      handleCloseEdit();
      setActiveTab(1);
    }
  };
  const handleEnableAccess = async (): Promise<void> => {
    if (!editUser) return;
    try {
      setSavingEmail(editUser.email);
      // Emits an ACCOUNT_ENABLED history event [1].
      await enableUserAccess(editUser, editAdminComment || undefined);
      editUser.approved = true;
      editUser.lastUpdated = new Date().toISOString();
      await fetchAllUsers();
      setSnackbar({
        open: true,
        message: `Re-enabled access for ${editUser.firstName} ${editUser.lastName}`,
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: `Failed to re-enable ${editUser.email}`,
        severity: 'error',
      });
    } finally {
      setSavingEmail(null);
      handleCloseEdit();
    }
  };

  const handleRestrictAccess = async (): Promise<void> => {
    if (!editUser) return;
    try {
      setSavingEmail(editUser.email);
      await restrictUserAccess(editUser, editAdminComment || undefined);
      editUser.lastUpdated = new Date().toISOString();
      setSnackbar({
        open: true,
        message: `Restricted access for ${editUser.firstName} ${editUser.lastName}`,
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: `Failed to restrict ${editUser.email}`,
        severity: 'error',
      });
    } finally {
      setSavingEmail(null);
      handleCloseEdit();
    }
  };


  const handleOpenReview = (row: UnifiedRequest): void => {
    setReviewRow(row);
    // For upgrades default to requestedTier; for new users default to the tier they selected at sign-up
    setReviewTier(((row.requestedTier || row.currentTier) as MembershipTier) || 'Tier 1');
    setReviewComment('');
    setReviewDialogOpen(true);
  };
  const handleCloseReview = (): void => {
    setReviewDialogOpen(false);
    setReviewRow(null);
    setReviewTier('Tier 1');
    setReviewComment('');
  };
  const handleApproveRequest = async (): Promise<void> => {
    if (!reviewRow) return;
    try {
      setSavingEmail(reviewRow.email);
      if (reviewRow.requestType === 'new-user') {
        await approveUser(reviewRow.user, reviewComment, reviewTier);
        await Promise.all([fetchAllUsers(), fetchUsers()]);
      } else {
        await resolveTierUpgrade({
          email: reviewRow.email,
          resolution: 'approved',
          assignedTier: reviewTier,
          originalRequestedTier: reviewRow.requestedTier as MembershipTier | undefined,
          comment: reviewComment,
          eventTimestamp: reviewRow.user.eventTimestamp!,
          orcidId: reviewRow.user.orcidId,
          currentUserTier: reviewRow.user.membershipTier,
        });
        await Promise.all([fetchAllUsers(), fetchUsers()]);
      }
      setSnackbar({ open: true, message: `Approved ${reviewRow.name}`, severity: 'success' });
    } catch {
      setSnackbar({
        open: true,
        message: `Failed to approve ${reviewRow.email}`,
        severity: 'error',
      });
    } finally {
      setSavingEmail(null);
      handleCloseReview();
      setActiveTab(0);
    }
  };
  const handleDenyRequest = async (): Promise<void> => {
    if (!reviewRow) return;
    try {
      setSavingEmail(reviewRow.email);
      if (reviewRow.requestType === 'new-user') {
        await denyUser(reviewRow.user, reviewComment);
        await Promise.all([fetchAllUsers(), fetchUsers()]);
      } else {
        await resolveTierUpgrade({
          email: reviewRow.email,
          resolution: 'denied',
          assignedTier: reviewRow.requestedTier as MembershipTier | undefined,
          originalRequestedTier: reviewRow.requestedTier as MembershipTier | undefined,
          comment: reviewComment,
          eventTimestamp: reviewRow.user.eventTimestamp!,
          currentUserTier: reviewRow.user.membershipTier,
        });
        await Promise.all([fetchAllUsers(), fetchUsers()]);
      }
      setSnackbar({
        open: true,
        message: `Denied request for ${reviewRow.name}`,
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: `Failed to deny ${reviewRow.email}`,
        severity: 'error',
      });
    } finally {
      setSavingEmail(null);
      handleCloseReview();
      setActiveTab(0);
    }
  };

  const renderContent = (): React.ReactElement => {
    if (!currentUser) {
      return (
        <CenteredContainer>
          <StyledAlert severity="error">
            You must be logged in to use this feature.
          </StyledAlert>
        </CenteredContainer>
      );
    }
    if (!currentUser.isNewUserApprover) {
      return (
        <CenteredContainer>
          <StyledAlert severity="error">
            You do not have access to this feature.
          </StyledAlert>
        </CenteredContainer>
      );
    }
    if (!initialLoadComplete) {
      return (
        <HorizontallyCentered>
          <CircularProgress text="Loading..." />
        </HorizontallyCentered>
      );
    }

    return (
      <>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)}>
            <Tab
              label={
                totalPendingCount > 0 ? (
                  <Badge badgeContent={totalPendingCount} color="error" sx={{ pr: 1.5 }}>
                    Pending Requests
                  </Badge>
                ) : (
                  'Pending Requests'
                )
              }
            />
            <Tab label="All Users" />
          </Tabs>
        </Box>

        {/* Search bar */}
        <Box sx={{ mb: 2, maxWidth: 400 }}>
          <TextField
            placeholder="Search by name, email, or affiliation…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Pending Requests tab */}
        {activeTab === 0 && (
          <PendingRequestsTab
            rows={pendingRows}
            searchText={searchText}
            isMobile={isMobile}
            isLoading={!initialLoadComplete}
            theme={theme}
            onReview={handleOpenReview}
          />
        )}

        {/* All Users tab */}
        {activeTab === 1 && (
          <AllUsersTab
            users={filteredAllUsers}
            searchText={searchText}
            isMobile={isMobile}
            isLoading={isLoading}
            savingEmail={savingEmail}
            reviewCommentByEmail={effectiveCommentByEmail}
            onEdit={handleOpenEdit}
          />
        )}
      </>
    );
  };

  return (
    <Box className={className} sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
      <CenteredContainer>
        <StyledH1>Manage Users & User Requests</StyledH1>
      </CenteredContainer>
      {currentUser?.isNewUserApprover && (
        <Box sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/profile')} size="small">
            Back to Profile
          </Button>
        </Box>
      )}

      {renderContent()}

      <EditUserDialog
        open={editDialogOpen}
        user={editUser}
        tier={editTier}
        adminComment={editAdminComment}
        savingEmail={savingEmail}
        onClose={handleCloseEdit}
        onTierChange={setEditTier}
        onAdminCommentChange={setEditAdminComment}
        onSave={handleSaveEdit}
        onEnableAccess={handleEnableAccess}
        onRestrictAccess={handleRestrictAccess}
      />

      <ReviewRequestDialog
        open={reviewDialogOpen}
        row={reviewRow}
        tier={reviewTier}
        comment={reviewComment}
        savingEmail={savingEmail}
        currentUser={currentUser ?? null} // currentUser should never be null
        onClose={handleCloseReview}
        onTierChange={setReviewTier}
        onCommentChange={setReviewComment}
        onApprove={handleApproveRequest}
        onDeny={handleDenyRequest}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '50vh !important', transform: 'translateY(-50%)' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%', '& .MuiAlert-message': { textAlign: 'center', flex: 1 } }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default observer(ManageUserMemberships);